import { ArrowLeft, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { checkMailbox, getMailbox } from "@/features/eservices/api";
import type { MailboxSummaryRow } from "@/features/eservices/types";

export function SecureMailboxPage() {
  const [summary, setSummary] = useState<MailboxSummaryRow[]>([]);
  const [lastCheck, setLastCheck] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    getMailbox()
      .then((res) => {
        setSummary(res.summary);
        setLastCheck(res.lastCheck);
      })
      .catch(() => toast.error("Could not load the mailbox."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  const check = async () => {
    setChecking(true);
    try {
      await checkMailbox();
      toast.success("Mailbox check queued for the connector.");
    } catch {
      toast.error("Could not queue the check.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/eservices"
            className="text-muted-foreground mb-1 flex items-center gap-1 text-sm hover:underline"
          >
            <ArrowLeft className="size-3" /> e-Services
          </Link>
          <h1 className="text-foreground text-2xl font-semibold">Secure Mailbox</h1>
          <p className="text-muted-foreground text-sm">
            Last checked:{" "}
            {lastCheck ? new Date(lastCheck).toLocaleString() : "never"}
          </p>
        </div>
        <Button onClick={check} disabled={checking}>
          <RefreshCw className="mr-1 size-4" /> Check mailbox
        </Button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <Card className="gap-0 overflow-hidden p-0">
          <div className="bg-muted/40 border-b px-4 py-2 text-sm font-semibold">
            Latest transcripts downloaded
          </div>
          {summary.length === 0 ? (
            <p className="text-muted-foreground p-4 text-sm">
              No downloaded transcripts yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Downloaded</TableHead>
                  <TableHead>Last Check</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.map((row) => (
                  <TableRow key={row.client}>
                    <TableCell className="font-medium">{row.clientName}</TableCell>
                    <TableCell>{row.count}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.lastCheck
                        ? new Date(row.lastCheck).toLocaleDateString()
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
